import { Button } from '@components/Button';
import moment from 'moment';
import { useNavigate } from 'react-router-dom';
import { HiArrowRight, HiClock } from 'react-icons/hi';
import { BillingCycle, Invoice } from './invoiceTypes';

export function InvoicePaymentDetails({
  isLsp,
  hasConfirmed,
  billingCycle,
  invoice,
  invoiceNotes,
  dueDate,
  isConfirming,
  handleConfirmInvoice,
  setHasPaid,
}: {
  isLsp?: boolean;
  hasConfirmed?: boolean;
  billingCycle: BillingCycle;
  invoice: Invoice;
  invoiceNotes: string;
  dueDate: string;
  isConfirming?: boolean;
  handleConfirmInvoice?: () => void;
  setHasPaid: React.Dispatch<React.SetStateAction<boolean>>;
}) {
  const navigate = useNavigate();
  const createdAt = moment(invoice.createdAt);
  const today = moment();
  const elapsedDays = today.diff(createdAt, 'days');
  const hasPaid = invoice.status === 'Paid';

  return (
    <>
      {/* Payment Details Card */}
      <div className='mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
        <h3 className='text-lg font-semibold text-gray-900 mb-6'>
          Payment Details
        </h3>
        <div className='grid grid-cols-1 md:grid-cols-2 gap-8'>
          {/* Bank Details */}
          <div className='space-y-4'>
            <div className='border-b border-gray-200 pb-2'>
              <span className='text-sm font-medium text-gray-500 uppercase tracking-wider'>
                Bank Details
              </span>
            </div>
            <div className='space-y-3'>
              <div className='flex justify-between'>
                <span className='text-sm text-gray-500'>Beneficiary Name</span>
                <span className='text-sm font-medium text-gray-900'>
                  {isLsp ? billingCycle.lspBusinessName : 'VCL Trading Europe'}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-gray-500'>Bank</span>
                <span className='text-sm font-medium text-gray-900'>
                  {isLsp ? billingCycle.lspBankName : 'NatWest'}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-gray-500'>Sort Code</span>
                <span className='text-sm font-medium text-gray-900'>
                  {isLsp
                    ? billingCycle.lspBankSortCode
                    : import.meta.env.VITE_VCL_SORT_CODE}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-gray-500'>Account Number</span>
                <span className='text-sm font-medium text-gray-900'>
                  {isLsp
                    ? billingCycle.lspBankAccountNumber
                    : import.meta.env.VITE_VCL_ACCOUNT_NUMBER}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-gray-500'>BIC/SWIFT</span>
                <span className='text-sm font-medium text-gray-900'>
                  {isLsp
                    ? billingCycle.lspBankSwiftCode
                    : import.meta.env.VITE_VCL_BIC_SWIFT}
                </span>
              </div>
              <div className='flex justify-between'>
                <span className='text-sm text-gray-500'>IBAN</span>
                <span className='text-sm font-medium text-gray-900'>
                  {isLsp
                    ? billingCycle.lspBankIban
                    : import.meta.env.VITE_VCL_IBAN}
                </span>
              </div>
            </div>
          </div>

          {/* Payment Status & Actions */}
          <div className='space-y-4'>
            <div className='border-b border-gray-200 pb-2'>
              <span className='text-sm font-medium text-gray-500 uppercase tracking-wider'>
                Payment Status
              </span>
            </div>

            {hasConfirmed && !hasPaid && (
              <div className='p-4 bg-yellow-50 border border-yellow-200 rounded-lg'>
                <div className='flex items-center gap-2'>
                  <HiClock className='w-5 h-5 text-yellow-600' />
                  <span className='text-sm font-medium text-yellow-800'>
                    Payment Due: {moment(dueDate).format('DD/MM/YYYY')}
                  </span>
                </div>
              </div>
            )}

            {hasPaid && invoice.payments.length > 0 && (
              <div className='space-y-4'>
                {invoice.payments.map((payment) => (
                  <div key={payment.id}>
                    <div className='p-4 bg-green-50 border border-green-200 rounded-lg mb-4'>
                      <p className='text-sm text-green-800'>
                        Payment received on{' '}
                        {moment(payment.createdAt).format('DD/MM/YYYY')}
                      </p>
                      <p className='text-sm text-green-700'>
                        Reference: {payment.paymentReference}
                      </p>
                    </div>
                  </div>
                ))}

                <div className='border-b border-gray-200 pb-2 pt-2'>
                  <span className='text-sm font-medium text-gray-500 uppercase tracking-wider'>
                    Payment History
                  </span>
                </div>
                {invoice.payments.map((payment) => (
                  <div
                    key={payment.id}
                    className='p-4 bg-gray-50 border border-gray-200 rounded-lg'
                  >
                    <div className='flex items-center justify-between mb-3'>
                      <span className='text-sm font-medium text-gray-900'>
                        {payment.paymentReference}
                      </span>
                      <span className='text-sm font-semibold text-gray-900'>
                        {payment.currencySymbol}
                        {payment.amount.toFixed(2)}
                      </span>
                    </div>
                    <div className='flex items-center justify-between'>
                      <div className='flex items-center gap-4'>
                        <span className='text-sm text-gray-500'>
                          {moment(payment.createdAt).format('DD/MM/YYYY')}
                        </span>
                        <span className='inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium bg-green-100 text-green-800'>
                          {payment.status}
                        </span>
                      </div>
                      <button
                        type='button'
                        onClick={() => navigate(`/payment/${payment.id}`)}
                        className='inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-700'
                      >
                        View
                        <HiArrowRight className='w-4 h-4' />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Action Buttons */}
            <div className='pt-4 space-y-3'>
              {!hasConfirmed && isLsp && (
                <Button
                  variant='primary'
                  size='lg'
                  onClick={handleConfirmInvoice}
                  disabled={isConfirming}
                >
                  {isConfirming ? 'Confirming...' : 'Confirm Invoice'}
                </Button>
              )}
              {hasConfirmed && !hasPaid && isLsp && (
                <Button
                  variant='primary'
                  size='lg'
                  onClick={() => {
                    setHasPaid(true);
                  }}
                  disabled={false} // Set this to true when demo is over
                >
                  Finance
                </Button>
              )}
              {!hasPaid && !isLsp && (
                <Button
                  variant='primary'
                  size='lg'
                  onClick={() => {
                    navigate(`/invoice/pay?billingCycleId=${billingCycle.id}`);
                  }}
                >
                  Pay Invoice
                </Button>
              )}
              {!hasConfirmed && isLsp && (
                <p className='text-sm text-gray-500'>
                  Elapsed from PI issue date:{' '}
                  <span className='font-medium'>{elapsedDays} days</span>
                </p>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Invoice Notes Section */}
      {!hasConfirmed && isLsp && (
        <div className='mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
          <label
            htmlFor='comments'
            className='block text-sm font-medium text-gray-700 mb-2'
          >
            Invoice Notes
          </label>
          <textarea
            id='comments'
            name='comments'
            rows={4}
            className='block w-full rounded-lg border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500 sm:text-sm'
            placeholder='Enter any additional invoice notes here...'
          />
        </div>
      )}

      {hasConfirmed && invoiceNotes && invoiceNotes.length > 0 && (
        <div className='mt-6 bg-white rounded-lg shadow-sm border border-gray-200 p-6'>
          <h4 className='text-sm font-medium text-gray-700 mb-2'>
            Invoice Notes
          </h4>
          <p className='text-sm text-gray-600'>{invoiceNotes}</p>
        </div>
      )}
    </>
  );
}
